import React, { useMemo } from 'react';
import { ExternalLink, PlayCircle } from 'lucide-react';

/**
 * EventVideoEmbed
 * Renderiza el contenido de un campo de vídeo del evento. Acepta:
 *  - URL directa (YouTube, Vimeo, Dailymotion, Bunny.net, etc.) → se transforma a embed.
 *  - HTML con <iframe …> → se renderiza tal cual (sanitizado a iframe-only).
 *  - Shortcode genérico [video src="…"] → se intenta extraer la URL.
 *  - Cualquier otro contenido → se muestra como link externo.
 */

const SAFE_IFRAME_HOSTS = [
  'youtube.com',
  'youtube-nocookie.com',
  'youtu.be',
  'player.vimeo.com',
  'vimeo.com',
  'dailymotion.com',
  'geo.dailymotion.com',
  'bunnycdn.com',
  'mediadelivery.net',
  'iframe.mediadelivery.net',
  'b-cdn.net',
  'wistia.com',
  'fast.wistia.net',
  'twitch.tv',
];

function urlToEmbed(url) {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    // YouTube
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname.startsWith('/watch')) {
        const id = u.searchParams.get('v');
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
      if (u.pathname.startsWith('/shorts/')) {
        return `https://www.youtube.com/embed/${u.pathname.split('/')[2]}`;
      }
      if (u.pathname.startsWith('/embed/')) return url;
    }
    // Vimeo
    if (host === 'vimeo.com') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
    if (host === 'player.vimeo.com') return url;
    // Dailymotion
    if (host === 'dailymotion.com' || host === 'www.dailymotion.com') {
      // formats: /video/{id}, /embed/video/{id}
      const parts = u.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('video');
      if (idx >= 0 && parts[idx + 1]) return `https://geo.dailymotion.com/player.html?video=${parts[idx + 1]}`;
    }
    // Bunny.net iframe.mediadelivery.net/play/{lib}/{guid}
    if (host.endsWith('mediadelivery.net') || host.endsWith('b-cdn.net')) {
      return url;
    }
    // Twitch
    if (host === 'twitch.tv' || host === 'www.twitch.tv') {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts[0] === 'videos' && parts[1]) {
        return `https://player.twitch.tv/?video=v${parts[1]}&parent=${window.location.hostname}`;
      }
    }
    return null;
  } catch {
    return null;
  }
}

function extractSrcFromIframe(html) {
  const m = html.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
  return m ? m[1] : null;
}

function isAllowedIframeSrc(src) {
  try {
    const u = new URL(src);
    const host = u.hostname.replace(/^www\./, '');
    return SAFE_IFRAME_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

function extractFromShortcode(value) {
  // [video src="..."]  o  [iframe src="..."]  o  [embed]URL[/embed]
  const srcMatch = value.match(/src=["']([^"']+)["']/i);
  if (srcMatch) return srcMatch[1];
  const embedMatch = value.match(/\[embed\]([^\[]+)\[\/embed\]/i);
  if (embedMatch) return embedMatch[1].trim();
  return null;
}

function isDirectBunnyMp4(src) {
  try {
    const u = new URL(src);
    const host = (u.hostname || '').toLowerCase();
    const path = (u.pathname || '').toLowerCase();
    return host.endsWith('.b-cdn.net') && /\.mp4$/.test(path);
  } catch { return false; }
}

function resolveEmbed(rawValue) {
  if (!rawValue) return { kind: 'empty' };
  const value = String(rawValue).trim();

  // iframe directo
  if (/<iframe[\s>]/i.test(value)) {
    const src = extractSrcFromIframe(value);
    if (src && isDirectBunnyMp4(src)) {
      return { kind: 'mp4', src };
    }
    if (src && isAllowedIframeSrc(src)) {
      return { kind: 'iframe', src };
    }
    return { kind: 'unsafe' };
  }

  // Shortcode
  if (value.startsWith('[') && value.includes(']')) {
    const inner = extractFromShortcode(value);
    if (inner) {
      if (isDirectBunnyMp4(inner)) return { kind: 'mp4', src: inner };
      const emb = urlToEmbed(inner);
      if (emb) return { kind: 'iframe', src: emb };
      if (isAllowedIframeSrc(inner)) return { kind: 'iframe', src: inner };
      return { kind: 'link', href: inner };
    }
    return { kind: 'unsupported' };
  }

  // URL directa
  if (/^https?:\/\//i.test(value)) {
    if (isDirectBunnyMp4(value)) return { kind: 'mp4', src: value };
    const emb = urlToEmbed(value);
    if (emb) return { kind: 'iframe', src: emb };
    if (isAllowedIframeSrc(value)) return { kind: 'iframe', src: value };
    return { kind: 'link', href: value };
  }

  return { kind: 'unsupported' };
}

export function EventVideoEmbed({ value, label, testId }) {
  const resolved = useMemo(() => resolveEmbed(value), [value]);

  if (resolved.kind === 'empty') return null;

  if (resolved.kind === 'mp4') {
    // MP4 directo de Bunny: reproducir con <video> protegido (sin iframe),
    // ocultando el botón de descarga nativo del navegador y bloqueando el
    // menú contextual (Guardar vídeo como…).
    return (
      <div
        className="rounded-2xl overflow-hidden border border-[var(--ns-border)] bg-black"
        data-testid={testId}
      >
        {label ? (
          <div className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-[var(--ns-gold-700)] font-semibold bg-[var(--ns-bg-2)] border-b border-[var(--ns-border)]">
            {label}
          </div>
        ) : null}
        <div className="aspect-video w-full">
          <video
            src={resolved.src}
            controls
            controlsList="nodownload noremoteplayback noplaybackrate"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            playsInline
            preload="metadata"
            className="w-full h-full object-contain bg-black"
          />
        </div>
      </div>
    );
  }

  if (resolved.kind === 'iframe') {
    return (
      <div
        className="rounded-2xl overflow-hidden border border-[var(--ns-border)] bg-black"
        data-testid={testId}
      >
        {label ? (
          <div className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-[var(--ns-gold-700)] font-semibold bg-[var(--ns-bg-2)] border-b border-[var(--ns-border)]">
            {label}
          </div>
        ) : null}
        <div className="aspect-video w-full">
          <iframe
            src={resolved.src}
            title={label || 'Vídeo'}
            className="w-full h-full block"
            frameBorder="0"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  if (resolved.kind === 'link') {
    return (
      <div
        className="rounded-2xl border border-[var(--ns-border)] bg-white p-5 flex items-center gap-3"
        data-testid={testId}
      >
        <div className="h-11 w-11 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
          <PlayCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold">{label || 'Vídeo'}</p>
          <a
            href={resolved.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[var(--ns-text)] hover:text-[var(--ns-gold-700)] underline truncate inline-flex items-center gap-1"
          >
            Abrir en proveedor externo <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border border-[var(--ns-border)] bg-white p-5 text-sm text-[var(--ns-text-muted)]"
      data-testid={testId}
    >
      <p className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold mb-1">{label || 'Vídeo'}</p>
      Contenido de vídeo no soportado o no permitido por el proveedor.
    </div>
  );
}

export default EventVideoEmbed;
